import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookCard } from '@/components/books/BookCard'
import { FilterPanel } from '@/components/books/FilterPanel'
import { SearchInput } from '@/components/books/SearchInput'
import { Button } from '@/components/ui'
import { useBooksWithFilters } from '@/hooks/books/useBooksWithFilters'
import { useUserPreferredGenres } from '@/hooks/genres/useUserPreferredGenres'
import { useLibraryBooks } from '@/hooks/insights'
import { useCreateRoulette } from '@/hooks/roulette/useRoulettes'
import { useFilterPersistence } from '@/hooks/useFilterPersistence'
import { suggestRouletteBooks } from '@/lib/insights'
import { captureError } from '@/lib/monitoring'
import type { Book } from '@/types/book'
import type { RouletteWithStats } from '@/types/roulette'

const SUGGESTION_COUNT = 8

interface RouletteCreateProps {
  onCancel: () => void
  onSuccess: (roulette: RouletteWithStats) => void
}

const ROULETTE_COLORS = [
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5A00', // Orange
  '#6366F1', // Indigo
]

const ROULETTE_ICONS = [
  '🎲',
  '📚',
  '🎭',
  '🌟',
  '🎪',
  '🎨',
  '🎯',
  '🎊',
  '📖',
  '✨',
  '🎈',
  '🌈',
  '🎵',
  '🎰',
  '🎢',
  '🎡',
]

export function RouletteCreate({ onCancel, onSuccess }: RouletteCreateProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedColor, setSelectedColor] = useState(ROULETTE_COLORS[0])
  const [selectedIcon, setSelectedIcon] = useState(ROULETTE_ICONS[0])
  const [selectedBooks, setSelectedBooks] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)

  const { filters, updateFilters } = useFilterPersistence('roulette-create')

  const {
    data: booksData,
    isLoading: booksLoading,
    error: booksError,
  } = useBooksWithFilters({
    filters: {
      ...filters,
      statuses: ['unread', 'reading'], // Only books to read or currently reading, exclude read books and wishlist
    },
    page,
    pageSize: 20,
  })

  const createRouletteMutation = useCreateRoulette()

  // Smart suggestions: unread books from genres/authors the user rated well
  const { data: libraryBooks = [] } = useLibraryBooks()
  const { data: preferredGenres = [] } = useUserPreferredGenres()
  const [suggestedCount, setSuggestedCount] = useState<number | null>(null)

  const handleSuggest = () => {
    const ids = suggestRouletteBooks(
      libraryBooks,
      preferredGenres.map((g) => g.id),
      SUGGESTION_COUNT
    )
    setSelectedBooks(new Set(ids))
    setSuggestedCount(ids.length)
  }

  const handleSearchChange = (value: string) => {
    updateFilters({ ...filters, search: value })
  }

  const handleBookToggle = (bookId: string) => {
    const newSelected = new Set(selectedBooks)
    if (newSelected.has(bookId)) {
      newSelected.delete(bookId)
    } else {
      newSelected.add(bookId)
    }
    setSelectedBooks(newSelected)
  }

  const handleSelectAll = () => {
    if (!booksData?.books) return

    const allBookIds = new Set([...selectedBooks, ...booksData.books.map((book) => book.id)])
    setSelectedBooks(allBookIds)
  }

  const handleDeselectAll = () => {
    if (!booksData?.books) return

    const newSelected = new Set(selectedBooks)
    booksData.books.forEach((book) => newSelected.delete(book.id))
    setSelectedBooks(newSelected)
  }

  const handleSubmit = async () => {
    if (!name.trim() || selectedBooks.size < 1) return

    try {
      const result = await createRouletteMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        color: selectedColor,
        icon: selectedIcon,
        book_ids: Array.from(selectedBooks),
      })

      // Transform to RouletteWithStats for consistency
      const rouletteWithStats: RouletteWithStats = {
        ...result,
        books: [],
        book_count: selectedBooks.size,
        total_spins: 0,
        last_spin_at: undefined,
        current_reading: undefined,
      }

      onSuccess(rouletteWithStats)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to create roulette' })
    }
  }

  const isFormValid = name.trim().length > 0 && selectedBooks.size >= 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            🎲 {t('roulette.createNew')}
          </h1>
          <p className="text-muted-foreground mt-2">{t('roulette.createRouletteDescription')}</p>
        </div>
        <Button onClick={onCancel} variant="outline" className="sm:flex-shrink-0">
          {t('common.cancel')}
        </Button>
      </div>

      {/* Roulette Configuration */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          {t('roulette.rouletteConfiguration')}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="roulette-name"
                className="block text-sm font-medium text-foreground mb-2"
              >
                {t('roulette.nameRequired')}
              </label>
              <input
                id="roulette-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t('roulette.namePlaceholder')}
                maxLength={100}
              />
            </div>

            <div>
              <label
                htmlFor="roulette-description"
                className="block text-sm font-medium text-foreground mb-2"
              >
                {t('roulette.descriptionOptional')}
              </label>
              <textarea
                id="roulette-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder={t('roulette.descriptionPlaceholder')}
                rows={3}
                maxLength={500}
              />
            </div>
          </div>

          {/* Visual Customization */}
          <div className="space-y-4">
            <div>
              <div className="block text-sm font-medium text-foreground mb-2">
                {t('roulette.colorTheme')}
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 gap-2">
                {ROULETTE_COLORS.map((color) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 transition-all ${
                      selectedColor === color
                        ? 'border-foreground scale-110'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="block text-sm font-medium text-foreground mb-2">
                {t('roulette.icon')}
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-6 xl:grid-cols-8 gap-1 sm:gap-2">
                {ROULETTE_ICONS.map((icon) => (
                  <button
                    type="button"
                    key={icon}
                    onClick={() => setSelectedIcon(icon)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg border transition-all text-sm sm:text-lg ${
                      selectedIcon === icon
                        ? 'border-primary bg-primary/10 scale-110'
                        : 'border-input hover:border-border hover:bg-muted/50'
                    }`}
                    title={icon}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h3 className="text-sm font-medium text-foreground mb-3">{t('roulette.preview')}</h3>
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-xl"
              style={{ backgroundColor: `${selectedColor}20` }}
            >
              {selectedIcon}
            </div>
            <div>
              <div className="font-semibold text-foreground">
                {name || t('roulette.rouletteName')}
              </div>
              {description && (
                <div className="text-sm text-muted-foreground mt-1">{description}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Book Selection */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">
              {t('roulette.selectBooksMessage', { count: selectedBooks.size })}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={handleSuggest}
                size="sm"
                disabled={libraryBooks.length === 0}
                className="flex-1 sm:flex-none"
                title={t('roulette.suggestHint')}
              >
                ✨ {t('roulette.suggestBooks')}
              </Button>
              <Button
                onClick={handleSelectAll}
                variant="outline"
                size="sm"
                disabled={!booksData?.books?.length}
                className="flex-1 sm:flex-none"
              >
                {t('common.selectAll')}
              </Button>
              <Button
                onClick={handleDeselectAll}
                variant="outline"
                size="sm"
                disabled={!booksData?.books?.length}
                className="flex-1 sm:flex-none"
              >
                {t('common.deselectAll')}
              </Button>
            </div>
          </div>

          {suggestedCount !== null && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4 text-sm text-foreground">
              {suggestedCount === 0
                ? t('roulette.noSuggestions')
                : t('roulette.suggestedBooks', { count: suggestedCount })}
            </div>
          )}

          {selectedBooks.size < 1 && (
            <div className="bg-warning/10 border border-warning rounded-lg p-3 mb-4">
              <div className="text-warning text-sm">{t('roulette.minimumBooksWarning')}</div>
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="px-6 pb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <SearchInput
              value={filters.search}
              onChange={handleSearchChange}
              placeholder={t('search.searchByTitleAuthor')}
              className="flex-1"
            />
            <FilterPanel
              filters={filters}
              onFiltersChange={updateFilters}
              hideStatusFilter={true}
              hideRatingFilter={true}
              compact={true}
              className="w-full sm:w-auto"
            />
          </div>
        </div>

        {/* Books Grid */}
        <div className="p-6 pt-0">
          {booksError && (
            <div className="text-center py-8">
              <div className="text-error mb-2">{t('roulette.errorLoadingBooks')}</div>
              <div className="text-sm text-muted-foreground">{booksError.message}</div>
            </div>
          )}

          {booksLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                <div
                  key={`book-skeleton-loading-${item}`}
                  className="bg-muted rounded-lg h-64 animate-pulse"
                />
              ))}
            </div>
          )}

          {booksData && !booksLoading && (
            <>
              {booksData.books.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <div className="text-4xl mb-2">📚</div>
                    <div className="text-lg font-medium">{t('roulette.noBooksMessage')}</div>
                    <div className="text-sm">{t('roulette.adjustFiltersMessage')}</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {booksData.books.map((book: Book) => (
                    <BookCard
                      key={book.id}
                      book={book}
                      context="roulette"
                      isSelected={selectedBooks.has(book.id)}
                      onToggleSelection={() => handleBookToggle(book.id)}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {booksData.pageCount > 1 && (
                <div className="flex justify-center mt-6">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      variant="outline"
                      size="sm"
                    >
                      {t('roulette.previous')}
                    </Button>
                    <span className="text-sm text-muted-foreground px-3">
                      {t('roulette.pageOf', { page, total: booksData.pageCount })}
                    </span>
                    <Button
                      onClick={() => setPage((p) => Math.min(booksData.pageCount, p + 1))}
                      disabled={page === booksData.pageCount}
                      variant="outline"
                      size="sm"
                    >
                      {t('roulette.next')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground order-2 sm:order-1">
          {selectedBooks.size > 0 && (
            <>
              {t('roulette.booksSelected', { count: selectedBooks.size })}
              {selectedBooks.size >= 1 && (
                <span className="text-success ml-2">{t('roulette.readyToCreate')}</span>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-3 order-1 sm:order-2">
          <Button onClick={onCancel} variant="outline" className="flex-1 sm:flex-none">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || createRouletteMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold flex-1 sm:flex-none"
          >
            {createRouletteMutation.isPending
              ? t('roulette.creating')
              : t('roulette.createRouletteButton')}
          </Button>
        </div>
      </div>
    </div>
  )
}
